import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TablePagination,
  CircularProgress,
  TableContainer,
  Button,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  Alert,
  Snackbar,
  Chip,
  Tooltip,
  Collapse,
} from "@mui/material";

import { motion, AnimatePresence } from "framer-motion";
import { border, borderLeft, borderRight, styled } from "@mui/system";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { green, red } from "@mui/material/colors";
import axios from "axios";
import { API_URL } from "../../../Config/api";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PendingActionsIcon from "@mui/icons-material/PendingActions";

/* ===================== STYLES ===================== */

/* ===================== TABLE CONFIG ===================== */

const TABLE_CONFIG = [
  { label: "Sr.No", flex: 0.5 },
  { label: "Event Code", flex: 1.5 },
  { label: "Start Date", flex: 1.2 },
  { label: "End Date", flex: 1.2 },
  { label: "Total Session", flex: 0.8 },
  { label: "Req Total Session", flex: 1 },
  { label: "Revalidation", flex: 1 },
  { label: "Prof Cost", flex: 1 },
  { label: "  Requested Cost", flex: 1.5 },
  { label: "Total Cost", flex: 1.2 },
  { label: "Paid Sessions", flex: 1.2 },
  { label: "Feedback", flex: 0.8 },
  { label: "Payment Taken", flex: 1.5 },
];
/* ===================== COMPONENT ===================== */

const ProfRequestPayoutmodal = ({ row, onActionSuccess }) => {
  const accessToken = localStorage.getItem("token");
  const [tableData, setTableData] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [actionLoading, setActionLoading] = useState(false);

  // Dialog states
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");
  const [approveRemark, setApproveRemark] = useState("");

  const [loading, setLoading] = useState(false);
  const [approneStatus, setApproneStatus] = useState(1);
  const [policeStatus, setPoliceStatus] = useState(1);

  const [approveCharges, setApproveCharges] = useState(0);
  const [policeCharges, setPoliceCharges] = useState(0);

  const [isSessionValid, setIsSessionValid] = useState(false);
  const [sessionMsg, setSessionMsg] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // success | error | warning | info
  });

  const isBankVerified =
    row?.prof_bank_verification === true ||
    row?.prof_bank_verification === "true";

  console.log("Bank Verification Status:", row?.prof_bank_verification);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const paginatedData = useMemo(() => {
    if (!Array.isArray(tableData)) return [];
    return tableData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [tableData, page, rowsPerPage]);
  const fetchDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/pay_out/payout_request_approve_reject_list_detail_view/${row.pay_req_id}/`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );

      const apiData = response.data;
      const normalizedData = Array.isArray(apiData)
        ? apiData
        : apiData?.data || [];

      if (normalizedData.length > 0) {
        const req = normalizedData[0];
        setApproneStatus(req.approne_charges_status ?? 1);
        setPoliceStatus(req.police_verification_charges_status ?? 1);
        setApproveCharges(req.approne_charges || 0);
        setPoliceCharges(req.police_verification_charges || 0);

        const mappedTableData = (req.events || []).map((eve) => ({
          pay_req_eve_id: eve.pay_req_eve_id,
          pay_req_id: req.pay_req_id,
          srv_prof_id: req.srv_prof_id,
          event_code: eve.event_code,
          service_start_date: eve.service_start_date,
          service_end_date: eve.service_end_date,
          total_sessions: eve.session_count,
          requested_session_count: eve.requested_session_count ?? 0,
          job_closure_session: eve.job_closure_session ?? 0,
          revalidated_session: eve.revalidation_session ?? 0,
          prof_cost: eve.prof_cost,
          prot_requested_cost: eve.total_requested_cost,
          total_prof_cost: eve.total_prof_cost,
          session_paid: eve.session_paid,
          feedback: !!eve.feed_back_status,
          payment_status_code: eve.payment_with_status,

          convenience: req.convenience || 0,
        }));

        setTableData(mappedTableData);
      }
    } catch (error) {
      console.error("View API error", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (row) fetchDetails();
  }, [row]);

  useEffect(() => {
    if (!row) return;

    fetchDetails();
  }, [row, accessToken]);

  const summary = useMemo(() => {
    const selectedItems = tableData || [];

    const totalRequested = selectedItems.reduce(
      (sum, item) => sum + Number(item.prot_requested_cost || 0),
      0,
    );

    const totalProfessionalCost = selectedItems.reduce(
      (sum, item) => sum + Number(item.total_prof_cost || 0),
      0,
    );

    // Sirf pehli row se convenience uthayein (kyunki ye pure request ka single amount hai)
    const conveyance =
      selectedItems.length > 0 ? Number(selectedItems[0].convenience || 0) : 0;

    const tds = totalProfessionalCost * 0.1;

    const netAmount =
      totalProfessionalCost -
      tds -
      Number(approveCharges || 0) -
      Number(policeCharges || 0) +
      conveyance;

    return {
      total_requested_cost: totalRequested, // key name consistency fix
      totalProfessionalCost,
      tds,
      conveyance,
      netAmount,
    };
  }, [tableData, approveCharges, policeCharges]);

  /* ===================== UNIFIED ACTION HANDLER ===================== */

  // API: Process Approve/Reject
 const processPayoutAction = async (status) => {
  const isApprove = status === 2;
  const currentRemark = isApprove ? approveRemark : rejectRemark;

  if (!currentRemark.trim()) {
    setSnackbar({
      open: true,
      message: "Remark is required",
      severity: "warning",
    });
    return;
  }

  try {
    setActionLoading(true);
    const payload = {
      pay_req_id: row.pay_req_id,
      srv_prof_id: row.srv_prof_id,
      total_requeststed_amount: summary.totalProfessionalCost.toFixed(2),
      TDS_deducted_amount: summary.tds.toFixed(2),
      convenience: summary.conveyance || 0,
      approne_charges: approveCharges || 0,
      approne_charges_status: approneStatus,
      police_verification_charges: policeCharges || 0,
      police_verification_charges_status: policeStatus,
      net_amount: summary.netAmount.toFixed(2),
      request_status: status,
      remark: currentRemark,
      events: tableData.map((item) => ({
        pay_req_eve_id: item.pay_req_eve_id,
        session_count: item.total_sessions,
        revalidation_session: item.revalidated_session,
        prof_cost: item.prof_cost,
        total_prof_cost: item.total_prof_cost,
        payment_with_status: item.payment_status_code,
      })),
    };

    await axios.post(
      `${API_URL}/pay_out/payout_request_approve_reject/`,
      payload,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    
    setOpenApproveDialog(false);
    setOpenRejectDialog(false);
    
    setSnackbar({
      open: true,
      message: `Payout ${isApprove ? "Approved" : "Rejected"} Successfully!`,
      severity: "success",
    });

    
    setTimeout(() => {
    
      setApproveRemark("");
      setRejectRemark("");
      setTableData([]);

      
      if (onActionSuccess) {
        onActionSuccess();
      } else {
        fetchDetails();
      }
    }, 1500); 

  } catch (err) {
    setSnackbar({
      open: true,
      message: "API Action Failed. Please try again.",
      severity: "error",
    });
  } finally {
    setActionLoading(false);
  }
};

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN");
  };

  const sessionValidation = useMemo(() => {
    if (!tableData.length) {
      setIsSessionValid(false);
      setSessionMsg("No session data available");
      return false;
    }

    // check all rows
    const hasMismatch = tableData.some(
      (item) =>
        Number(item.requested_session_count) !==
        Number(item.revalidated_session),
    );

    if (hasMismatch) {
      setIsSessionValid(false);
      setSessionMsg(
        " Total Requested  Session Count And Revalidation Session Must Be Equal For Approval",
      );
      return false;
    } else {
      setIsSessionValid(true);
      setSessionMsg("");
      return true;
    }
  }, [tableData]);

  if (!row) return null;

  if (loading) {
    return (
      <Box sx={{ height: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* ===================== BANK VERIFICATION WARNING ===================== */}
      {isBankVerified ? (
        <Alert
          severity="success"
          variant="outlined"
          icon={<CheckCircleIcon sx={{ color: green[600] }} />}
          sx={{
            mb: 2,
            borderRadius: 2,
            fontWeight: 600,
            backgroundColor: "#f0fdf4", // Light green background
            borderColor: green[200],
          }}
        >
          Verification Success: Professional's bank account is verified.
        </Alert>
      ) : (
        <Alert
          severity="warning"
          variant="filled"
          icon={<WarningAmberIcon fontSize="inherit" />}
          sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}
        >
          Attention: Bank details are NOT verified. Payout approval is blocked.
        </Alert>
      )}
      {/* ===================== TABLE CARD ===================== */}
      <Card sx={{ p: 2, borderRadius: 2 }}>
        <TableContainer
          sx={{
            width: "100%",
            overflowX: "auto",
            maxHeight: "50vh",
            scrollbarWidth: "thin",
          }}
        >
          <Table
            stickyHeader
            sx={{
              minWidth: 1200,
              borderCollapse: "collapse",
              display: "block", // Essential for flex alignment
            }}
          >
            <TableHead sx={{ display: "block", width: "100%" }}>
              <TableRow
                sx={{ display: "flex", width: "100%", alignItems: "stretch" }}
              >
                {TABLE_CONFIG.map((col, i) => (
                  <TableCell
                    key={col.label}
                    sx={{
                      flex: col.flex,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#a6b4be",
                      fontWeight: 700,
                      color: "#000",
                      py: 1,
                      boxSizing: "border-box",
                      borderRight:
                        i === TABLE_CONFIG.length - 1
                          ? "none"
                          : "1px solid #fff",
                      borderTopLeftRadius: i === 0 ? 12 : 0,
                      borderTopRightRadius:
                        i === TABLE_CONFIG.length - 1 ? 12 : 0,
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody sx={{ display: "block", width: "100%" }}>
              {paginatedData.map((item, index) => {
                const rowIndex = page * rowsPerPage + index;

                // Flex values must match TABLE_CONFIG exactly
                const rowCells = [
                  { content: rowIndex + 1, flex: 0.5 },
                  { content: item.event_code, flex: 1.5 },
                  { content: formatDate(item.service_start_date), flex: 1.1 },
                  { content: formatDate(item.service_end_date), flex: 1.1 },
                  { content: item.total_sessions, flex: 0.8 },
                  {
                    content: item.requested_session_count,
                    flex: 1,
                  },
                  { content: item.revalidated_session ?? 0, flex: 1 },
                  { content: `₹${item.prof_cost}`, flex: 1 },
                  {
                    content:
                      item.prot_requested_cost !== undefined
                        ? `₹${item.prot_requested_cost}`
                        : "₹0",
                    flex: 1.5,
                  },
                  { content: `₹${item.total_prof_cost}`, flex: 1.2 },
                  { content: item.session_paid, flex: 1.2 },
                  {
                    flex: 0.8,
                    content: (
                      <Tooltip
                        title={
                          item.feedback
                            ? "Feedback Received"
                            : "Pending Feedback"
                        }
                        arrow
                        placement="top"
                      >
                        <span>
                          {item.feedback ? (
                            <CheckCircleIcon
                              color="success"
                              sx={{ fontSize: "20px" }}
                            />
                          ) : (
                            <PendingActionsIcon
                              color="action"
                              sx={{ fontSize: "20px" }}
                            />
                          )}
                        </span>
                      </Tooltip>
                    ),
                  },
                  {
                    flex: 1.5,
                    content: (
                      <Tooltip
                        title={`Payment Mode: ${
                          item.payment_status_code === 1
                            ? "Unpaid"
                            : item.payment_status_code === 2
                              ? "Professional"
                              : item.payment_status_code === 3
                                ? "By Desk"
                                : item.payment_status_code === 4
                                  ? "Complimentary"
                                  : "Unknown"
                        }`}
                        arrow
                        placement="top"
                      >
                        <Chip
                          label={
                            item.payment_status_code === 1
                              ? "Unpaid"
                              : item.payment_status_code === 2
                                ? "By Prof"
                                : item.payment_status_code === 3
                                  ? "By Desk"
                                  : item.payment_status_code === 4
                                    ? "Complimentary"
                                    : "Unknown"
                          }
                          size="small"
                          sx={{
                            fontWeight: 600,
                            cursor: "pointer", // Isse user ko pata chalega ki ye hoverable hai
                            backgroundColor:
                              item.payment_status_code === 1
                                ? "#ffebee"
                                : "#e8f5e9",
                            color:
                              item.payment_status_code === 1
                                ? "#c62828"
                                : "#2e7d32",
                          }}
                        />
                      </Tooltip>
                    ),
                  },
                ];

                return (
                  <TableRow
                    key={rowIndex}
                    sx={{
                      display: "flex",
                      width: "100%",
                      mt: 1,
                      backgroundColor: "#fff",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      borderRadius: 2,
                      mb: 1,
                      "& td": { border: "none" },
                    }}
                  >
                    {rowCells.map((cell, cellIdx) => (
                      <TableCell
                        key={cellIdx}
                        sx={{
                          flex: cell.flex,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          py: 1,
                          boxSizing: "border-box",
                          minWidth: 0,
                        }}
                      >
                        {cell.content}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          sx={{
            overflow: "hidden",
            "& .MuiTablePagination-toolbar": { paddingRight: 1 },
          }}
          component="div"
          count={tableData.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Card>

      <Collapse
        in={!isSessionValid && tableData.length > 0}
        timeout={500}
        orientation="vertical"
      >
        <Alert
          severity="warning"
          sx={{
            mt: 2,
            borderRadius: 2,
            animation: "fadeSlideIn 0.5s ease",
          }}
        >
          {sessionMsg}
        </Alert>
      </Collapse>

      {/* ===================== SUMMARY ===================== */}
      <AnimatePresence>
        {tableData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <Card
              sx={{
                mt: 3,
                borderRadius: 2,
                background: "#FAFAFA",
                overflow: "hidden",
              }}
            >
              <Table
                size="small"
                sx={{
                  minWidth: 800,
                  "& th, & td": {
                    textAlign: "center",
                    fontSize: 13,
                    borderBottom: "1px solid #E0E0E0",
                    p: 1.5,
                  },
                  "& th": {
                    fontWeight: 700,
                    backgroundColor: "#F1F8E9",
                    color: "#33691E",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Total Requested Cost</TableCell>
                    <TableCell>Total Professional Cost</TableCell>
                    <TableCell>TDS (10%)</TableCell>
                    <TableCell>Conveyance</TableCell>
                    {/* <TableCell>Approne</TableCell>
                    <TableCell>Police Verification</TableCell> */}
                    <TableCell>Net Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {/* Safe check for total_requested_amount */}₹
                      {(summary?.total_requested_cost ?? 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      ₹{(summary?.totalProfessionalCost ?? 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ color: "#d32f2f" }}>
                      - ₹{(summary?.tds ?? 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ color: "#2E7D32" }}>
                      + ₹{(summary?.conveyance ?? 0).toFixed(2)}
                    </TableCell>

                    {/* Input cells remain same */}
                    {/* <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={approveCharges}
                        onChange={(e) => setApproveCharges(e.target.value)}
                        disabled={approneStatus === 1}
                        inputProps={{
                          style: { textAlign: "center", fontSize: 13 },
                        }}
                        sx={{
                          width: "100px",
                          backgroundColor:
                            approneStatus === 1 ? "#F2F2F2" : "#fff",
                          "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        }}
                      />
                    </TableCell> */}
                    {/* <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={policeCharges}
                        onChange={(e) => setPoliceCharges(e.target.value)}
                        disabled={policeStatus === 1}
                        inputProps={{
                          style: { textAlign: "center", fontSize: 13 },
                        }}
                        sx={{
                          width: "100px",
                          backgroundColor:
                            policeStatus === 1 ? "#F2F2F2" : "#fff",
                          "& .MuiOutlinedInput-root": { borderRadius: 1 },
                        }}
                      />
                    </TableCell> */}

                    <TableCell
                      sx={{
                        fontWeight: 800,
                        fontSize: "15px !important",
                        color:
                          (summary?.netAmount ?? 0) <= 0
                            ? "#d32f2f"
                            : "#1B5E20",
                        backgroundColor:
                          (summary?.netAmount ?? 0) <= 0
                            ? "#FFEBEE"
                            : "#E8F5E9",
                      }}
                    >
                      ₹{(summary?.netAmount ?? 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===================== ACTION BAR ===================== */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          borderTop: "1px dashed #CFCFCF",
        }}
      >
        <Button
          variant="contained"
          color="error"
          disabled={actionLoading || tableData.length === 0}
          onClick={() => setOpenRejectDialog(true)}
          sx={{ textTransform: "none" }}
        >
          Reject
        </Button>
        <Tooltip
          title={
            actionLoading
              ? "Processing... Please wait"
              : tableData.length === 0
                ? "Cannot approve: No data available in table"
                : !isSessionValid
                  ? "Cannot approve: Requested Total Session count and revalidation must match"
                  : !isBankVerified
                    ? "Cannot approve: Professional's bank details are not verified"
                    : summary.netAmount <= 0
                      ? `Cannot approve: Net Amount (₹${summary.netAmount.toFixed(2)}) must be greater than zero`
                      : "Click to verify and approve this payout"
          }
          arrow
          placement="top"
        >
          <span>
            <Button
              variant="contained"
              color="success"
              disabled={
                actionLoading ||
                tableData.length === 0 ||
                !isSessionValid ||
                !isBankVerified ||
                summary.netAmount <= 0
              }
              onClick={() => setOpenApproveDialog(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                backgroundColor:
                  isBankVerified &&
                  summary.netAmount > 0 &&
                  isSessionValid &&
                  !actionLoading
                    ? "#2e7d32"
                    : "rgba(0, 0, 0, 0.12)",
                "&:hover": {
                  backgroundColor: "#1b5e20",
                },
              }}
            >
              {isBankVerified && summary.netAmount > 0
                ? "Verify & Approve"
                : "Approve (Blocked)"}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* ===================== DIALOGS ===================== */}
      <Dialog
        open={openApproveDialog}
        onClose={() => setOpenApproveDialog(false)}
      >
        <DialogTitle>Confirm Approval</DialogTitle>

        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Are you sure you want to approve this payout?
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Remark"
            placeholder="Enter approval remark"
            value={approveRemark}
            onChange={(e) => setApproveRemark(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenApproveDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => processPayoutAction(2)}
            disabled={actionLoading}
            sx={{ textTransform: "none" }}
          >
            Confirm Approve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openRejectDialog}
        onClose={() => setOpenRejectDialog(false)}
      >
        <DialogTitle>Reject Payout</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Remark"
            fullWidth
            multiline
            minRows={3}
            value={rejectRemark}
            onChange={(e) => setRejectRemark(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ textTransform: "none" }}
            onClick={() => setOpenRejectDialog(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => processPayoutAction(3)}
            disabled={actionLoading}
            sx={{ textTransform: "none" }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfRequestPayoutmodal;
